/**
 * Loading, failure and freshness of a read (batch 8 T12c1, #3033): a progress bar that is announced, an error with a retry, the time
 * the data was last confirmed (with its zone), and a plain statement when a refresh failed and what is shown may be out of date.
 */

import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { describeDispatchError } from './dispatchErrors';
import { formatDateTimeZone } from './dispatchFormat';

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation('procurementDispatch');
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }} role="status">
      <CircularProgress size={20} aria-label={label ?? t('state.loading')} />
      <Typography variant="body2">{label ?? t('state.loading')}</Typography>
    </Stack>
  );
}

export function ReadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation('procurementDispatch');
  return (
    <Alert
      severity="error"
      role="alert"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          {t('state.retry')}
        </Button>
      }
    >
      <Typography variant="body2" fontWeight={600}>
        {t('state.loadFailed')}
      </Typography>
      <Typography variant="body2">{describeDispatchError(t, error)}</Typography>
    </Alert>
  );
}

interface FreshnessProps {
  updatedAt: number;
  isStale: boolean;
  isRefetching: boolean;
  error?: unknown;
  onRefresh: () => void;
}

/** "Updated 14.05.12 EET" with a refresh button; a failed refresh says that what is shown may be out of date. */
export function Freshness({ updatedAt, isStale, isRefetching, error, onRefresh }: FreshnessProps) {
  const { t, i18n } = useTranslation('procurementDispatch');
  return (
    <Stack spacing={1}>
      {isStale && (
        <Alert severity="warning" role="alert">
          <Typography variant="body2">
            {t('state.stale', {
              time: formatDateTimeZone(
                updatedAt > 0 ? new Date(updatedAt).toISOString() : null,
                i18n.language
              ),
            })}
          </Typography>
          {error !== undefined && error !== null && (
            <Typography variant="body2">{describeDispatchError(t, error)}</Typography>
          )}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {updatedAt > 0 && !isStale && (
          <Typography variant="caption" color="text.secondary">
            {t('state.updated', {
              time: formatDateTimeZone(new Date(updatedAt).toISOString(), i18n.language),
            })}
          </Typography>
        )}
        <Button
          size="small"
          startIcon={isRefetching ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={onRefresh}
          disabled={isRefetching}
        >
          {t('state.refresh')}
        </Button>
      </Box>
    </Stack>
  );
}
